import XIcon from "@icons/x-close.svg?react";
import LockIcon from "@icons/lock-01.svg?react";
import CalendarIcon from "@icons/calendar.svg?react";
import HDDialog from "@/common/components/HDDialog";
import type { BridgeIntegrationId } from "@common/lib/api/bridge";
import {
    BRIDGE_UPGRADE_BOOKING_URL,
    bridgeMachineRequirement,
    bridgeUpgradeEyebrow,
    bridgeUpgradeFeatures,
    bridgeUpgradeIntro,
    softwarePossessive,
} from "./bridgeUpgradeCopy";
import { BridgeUpgradeFeatureIcon } from "./bridgeUpgradeIcons";

interface BridgeUpgradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onStartUpgrade: () => void;
    integration: BridgeIntegrationId | null;
}

// Requirements 1 and 3 only. The lock and calendar glyphs are `@icons/lock-01` and
// `@icons/calendar`; these two have no equivalent in `hd-icons`, so they stay inline.
/** Requirement 1 — the physical machine. */
const SERVER_ICON = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="shrink-0">
        <rect x="3" y="4" width="18" height="12" rx="1.5" stroke="currentColor" strokeWidth="2" />
        <path d="M8 20h8M12 16v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

/** Requirement 3 — the HappyDoc sign-in the installer hands off to. */
const KEY_ICON = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="shrink-0">
        <path
            d="M15.5 3a5.5 5.5 0 1 0-4.9 8L9 12.6H7v2H5v2H3v2.9h3.6l6-6a5.5 5.5 0 0 0 2.9.5Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <circle cx="16.5" cy="7" r="1.3" fill="currentColor" />
    </svg>
);

/**
 * The full-detail case for HappyBridge, opened from either the Integrations announcement card or
 * the app-wide strip. Purely presentational: eligibility is decided by `useBridgeUpgradeOffer()`
 * before either caller renders this at all.
 *
 * `min-h-[44px]` on both actions, as on every button in the offer's other two surfaces — see the
 * note in `BridgeUpgradeBanner` for why.
 */
export default function BridgeUpgradeModal({
    isOpen,
    onClose,
    onStartUpgrade,
    integration,
}: BridgeUpgradeModalProps): React.JSX.Element {
    const features = bridgeUpgradeFeatures(integration);
    const machine = bridgeMachineRequirement(integration);

    return (
        // `ariaLabel` because this modal passes no `header` — HDDialog wires `DialogTitle` only
        // inside that slot, and this layout puts its heading in the left column instead, so
        // without this the dialog is announced with no name at all.
        <HDDialog
            isOpen={isOpen}
            handleClose={onClose}
            className="!w-[95%] sm:!max-w-4xl max-h-[90vh]"
            ariaLabel="What you get by upgrading to HappyBridge"
            compact
        >
            {/* The handle other specs assert "the modal opened" on, so navigation tests do not hinge
                on the exact text of a marketing heading. This modal's own spec asserts the words. */}
            <div data-testid="bridge-upgrade-modal" className="-mx-6">
                {/* Our own close button: HDDialog only renders one inside `header`, which this
                    two-column layout does not use. Absolute against the DialogPanel (HDDialog sets
                    `position: relative` there), not this `-mx-6` wrapper, so the negative margin
                    does not shift it, and absolute so it paints over the right column's background
                    without a z-index fight.

                    Kept OUTSIDE the scrolling div below, or it scrolls away with the content:
                    bounding that div to `max-h-[80vh]` is what keeps the panel's own hardcoded
                    `overflow-y-auto` from ever having anything to activate on. */}
                <button
                    type="button"
                    aria-label="Close"
                    onClick={onClose}
                    className="absolute right-2 top-2 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-gray-400 shadow-sm backdrop-blur-sm hover:bg-gray-100 hover:text-gray-900 focused-inset"
                >
                    <XIcon className="h-5 w-5" />
                </button>

                <div className="flex max-h-[80vh] flex-col overflow-y-auto md:flex-row">
                    <div className="flex-grow p-8">
                        {/* Sentence case, not the uppercase+tracking it shipped with, and matching
                            the announcement card's eyebrow treatment — same words, from
                            `bridgeUpgradeEyebrow`, because the card is where the clinic clicked
                            from. Two weights, one string. */}
                        <span className="bg-max-100 text-max-900 rounded-full px-2.5 py-1 text-[11px] font-bold">
                            {bridgeUpgradeEyebrow(integration)}
                        </span>
                        <h2 className="mt-3 text-2xl font-semibold text-shadow-900">What you get by upgrading</h2>
                        <p className="mt-2 text-sm text-gray-500">
                            {/* Opens on the SAME sentence as the announcement card's body — one
                                function, so a clinic that read the card and clicked through reads
                                this modal as its second page rather than a restart of the pitch.
                                Why it stays neutral about their current setup is recorded on
                                `bridgeUpgradeIntro`. */}
                            {bridgeUpgradeIntro(integration)} It is a small program on your own server that talks to{" "}
                            {softwarePossessive(integration)} itself, with nothing in between.
                        </p>

                        <div className="mt-6 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                            {features.map((feature) => (
                                <div
                                    key={feature.id}
                                    data-testid="bridge-upgrade-feature"
                                    className="rounded-xl border border-gray-200 p-4"
                                >
                                    <BridgeUpgradeFeatureIcon id={feature.id} className="shrink-0 text-max-800" />
                                    <p className="mt-2 text-[13px] font-bold text-shadow-900">{feature.title}</p>
                                    <p className="mt-1 text-[13px] text-gray-500">{feature.detail}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex w-full flex-none flex-col border-gray-200 bg-gray-50 p-8 md:w-80 md:border-l">
                        <h3 className="text-base font-semibold text-shadow-900">Before you start</h3>
                        <p className="mt-1 text-[13px] text-gray-500">Requirements for upgrading:</p>

                        {/* Bold lead per item, so the column can be skimmed for what to go and find.
                            All three are the wizard's OWN requirements, not a list written here:
                            the machine rule restated on `RightComputerStep`, the administrator
                            rights `InstallConnectStep`'s stalled-install check asks about first, and
                            the sign-in that is step 2 of its install instructions. Outbound 443 is
                            deliberately absent — a clinic cannot check it standing at the machine,
                            and the wizard raises it where it actually bites. */}
                        <ul className="mt-4 flex flex-col gap-3.5">
                            <li data-testid="bridge-upgrade-requirement" className="flex items-start gap-2.5">
                                <span className="text-gray-500">{SERVER_ICON}</span>
                                <p className="text-[13px] text-gray-700">
                                    <span className="font-semibold text-shadow-900">{machine.serverNoun}.</span>{" "}
                                    {machine.rule}
                                </p>
                            </li>
                            <li data-testid="bridge-upgrade-requirement" className="flex items-start gap-2.5">
                                <LockIcon className="h-5 w-5 shrink-0 text-gray-500" />
                                <p className="text-[13px] text-gray-700">
                                    <span className="font-semibold text-shadow-900">Administrator permissions.</span>{" "}
                                    The installer needs them to run, so you&rsquo;ll want someone who can approve that
                                    on hand.
                                </p>
                            </li>
                            <li data-testid="bridge-upgrade-requirement" className="flex items-start gap-2.5">
                                <span className="text-gray-500">{KEY_ICON}</span>
                                <p className="text-[13px] text-gray-700">
                                    <span className="font-semibold text-shadow-900">Your HappyDoc login.</span> Partway
                                    through, the installer opens a browser window and asks you to sign in — have the
                                    username and password with you.
                                </p>
                            </li>
                        </ul>

                        <div className="mt-4 rounded-[10px] border border-tropicana-200 bg-tropicana-50 p-3.5">
                            {/* The INSTALL, which is what the clinic is committing to here. The
                                switchover keeps running afterwards and the wizard reports on that
                                separately once it's connected ("Switching after setup",
                                `steps/InstallConnectStep.tsx`), so this says the list may still be
                                settling rather than naming a second number that contradicts it. */}
                            <p className="text-[13px] font-semibold text-tropicana-950">
                                The install takes 5 to 10 minutes
                            </p>
                            <p className="mt-1 text-[13px] text-tropicana-900">
                                Your appointment list in HappyDoc may shift once setup is finished and HappyBridge takes
                                over. If your schedule is packed, do it at lunch or after hours.
                            </p>
                        </div>

                        <div className="mt-auto flex flex-col gap-2.5 pt-6">
                            <button
                                type="button"
                                className="primary-button w-full min-h-[44px]"
                                onClick={onStartUpgrade}
                            >
                                Start Upgrade
                            </button>
                            {/* `.secondary-button` (index.css), not a hand-rolled copy of it. This
                                reproduced the class utility for utility and dropped `focused` and
                                `transition-colors` on the way — a control that looked like the
                                app's without behaving like it, which is the exact fault the strip's
                                own button was fixed for. `min-h-[44px]` still overrides the class's
                                `py-1.5` for the tablet touch target. */}
                            <a
                                href={BRIDGE_UPGRADE_BOOKING_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="secondary-button min-h-[44px] w-full"
                            >
                                <CalendarIcon className="h-4 w-4 shrink-0" />
                                Schedule time with our team
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </HDDialog>
    );
}
